'use client';

import { Input } from '@headlessui/react';
import { parseFloatOrUndefined } from '@shared/lib/utils';
import { rNumber } from '@shared/lib/utils/formats';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';

export function DiamondQuantitativeRuler({
  values,
  lowLimit,
  highLimit,
  onSetLowLimitAction,
  onSetHighLimitAction,
}: {
  values: number[];
  lowLimit: number | undefined;
  highLimit: number | undefined;
  onSetLowLimitAction: (lowLimit: number | undefined) => void;
  onSetHighLimitAction: (highLimit: number | undefined) => void;
}) {
  const [lowLimitInput, setLowLimitInput] = useState<string | undefined>(lowLimit?.toString());
  const [highLimitInput, setHighLimitInput] = useState<string | undefined>(highLimit?.toString());
  const [draggingLeft, setDraggingLeft] = useState(false);
  const leftHandleRef = useRef<HTMLDivElement | null>(null);
  const [draggingRight, setDraggingRight] = useState(false);
  const rightHandleRef = useRef<HTMLDivElement | null>(null);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const [lowerLimitSliderPercentage, setLowerLimitSliderPercentage] = useState<number | undefined>(
    undefined
  );
  const [highLimitSliderPercentage, setHighLimitSliderPercentage] = useState<number | undefined>(
    undefined
  );

  const findIdx = (limit: number | undefined) => {
    if (limit === undefined) {
      return undefined;
    }
    return values.findIndex((value, idx) => {
      const nextValue: number = idx < values.length - 1 ? (values[idx + 1] as number) : Infinity;
      return (
        value === limit || ((value as number) < (limit as number) && nextValue > (limit as number))
      );
    });
  };

  const lowerLimitIdx = findIdx(lowLimit) || 0;
  const highLimitIdx = findIdx(highLimit) || values.length - 1;

  const calculateAndSetHighLimitSliderPercentage = useCallback(() => {
    if (highLimit === undefined) {
      setHighLimitSliderPercentage(100);
    } else {
      const highLimitFraction = Math.min(
        1,
        ((highLimit as number) - (values[highLimitIdx] as number)) /
          (highLimitIdx >= values.length - 2
            ? 3
            : (values[highLimitIdx + 1]! as number) - (values[highLimitIdx] as number))
      );
      setHighLimitSliderPercentage(((highLimitIdx + highLimitFraction) / values.length) * 100);
      rightHandleRef.current &&
        (rightHandleRef.current.style.left = `${((highLimitIdx + highLimitFraction) / values.length) * 100}%`);
    }
  }, [highLimit, values, highLimitIdx, setHighLimitSliderPercentage, rightHandleRef]);

  const calculateAndSetLowerLimitSliderPercentage = useCallback(() => {
    if (lowLimit === undefined) {
      setLowerLimitSliderPercentage(0);
    } else {
      const lowerLimitFraction = Math.min(
        1,
        ((lowLimit as number) - (values[lowerLimitIdx] as number)) /
          (lowerLimitIdx >= values.length - 1
            ? 3
            : (values[lowerLimitIdx + 1]! as number) - (values[lowerLimitIdx] as number))
      );
      setLowerLimitSliderPercentage(((lowerLimitIdx + lowerLimitFraction) / values.length) * 100);
      leftHandleRef.current &&
        (leftHandleRef.current.style.left = `${((lowerLimitIdx + lowerLimitFraction) / values.length) * 100}%`);
    }
  }, [lowLimit, values, setLowerLimitSliderPercentage, lowerLimitIdx, leftHandleRef]);

  const handleDragEnd = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!sliderRef.current || !leftHandleRef.current || !rightHandleRef.current) return;
      const parentWidth = sliderRef.current.getBoundingClientRect().width;
      const parentLeft = sliderRef.current.getBoundingClientRect().left;
      const boundaryXs = [
        parentLeft,
        ...values.map((_value, idx) => {
          return ((idx + 1) / values.length) * parentWidth + parentLeft;
        }),
      ];

      if (draggingLeft) {
        setDraggingLeft(false);
        const snappedIdx = findClosestLeftHandleIdx(
          boundaryXs,
          leftHandleRef.current.getBoundingClientRect().left
        );
        const newLowLimit = values[snappedIdx]!;
        calculateAndSetLowerLimitSliderPercentage();
        onSetLowLimitAction(newLowLimit);
      } else if (draggingRight) {
        setDraggingRight(false);
        const snappedIdx = findClosestRightHandleIdx(
          boundaryXs,
          rightHandleRef.current.getBoundingClientRect().left
        );
        const newHighLimit = snappedIdx + 1 >= values.length ? undefined : values[snappedIdx + 1]!;
        calculateAndSetHighLimitSliderPercentage();
        onSetHighLimitAction(newHighLimit);
      }
    },
    [
      draggingLeft,
      draggingRight,
      values,
      calculateAndSetLowerLimitSliderPercentage,
      calculateAndSetHighLimitSliderPercentage,
      onSetLowLimitAction,
      onSetHighLimitAction,
    ]
  );

  useEffect(() => {
    const handleDocumentMouseUp = (e: MouseEvent) => {
      if (draggingLeft || draggingRight) {
        handleDragEnd(e as unknown as React.MouseEvent);
      }
    };

    const handleDocumentTouchEnd = (e: TouchEvent) => {
      if (draggingLeft || draggingRight) {
        handleDragEnd(e as unknown as React.TouchEvent);
      }
    };

    document.addEventListener('mouseup', handleDocumentMouseUp);
    document.addEventListener('touchend', handleDocumentTouchEnd);
    return () => {
      document.removeEventListener('mouseup', handleDocumentMouseUp);
      document.removeEventListener('touchend', handleDocumentTouchEnd);
    };
  }, [draggingLeft, draggingRight, handleDragEnd]);

  useEffect(() => {
    calculateAndSetHighLimitSliderPercentage();
    calculateAndSetLowerLimitSliderPercentage();
  }, [
    lowLimit,
    highLimit,
    calculateAndSetHighLimitSliderPercentage,
    calculateAndSetLowerLimitSliderPercentage,
  ]);

  const findClosestLeftHandleIdx = (boundaryXs: number[], x: number) => {
    return boundaryXs.slice(0, -1).reduce((closestIdx, snapToX, idx) => {
      const currentDistance = Math.abs(snapToX - x);
      const closestDistance = Math.abs(boundaryXs[closestIdx]! - x);
      return currentDistance < closestDistance ? idx : closestIdx;
    }, 0);
  };

  const findClosestRightHandleIdx = (boundaryXs: number[], x: number) => {
    return boundaryXs.slice(1).reduce((closestIdx, snapToX, idx) => {
      const currentDistance = Math.abs(snapToX - x);
      const closestDistance = Math.abs(boundaryXs[closestIdx + 1]! - x);
      return currentDistance < closestDistance ? idx : closestIdx;
    }, 0);
  };

  const handleDrag = (e: React.MouseEvent | React.TouchEvent) => {
    if (!draggingLeft && !draggingRight) return;
    const x = 'clientX' in e ? e.clientX : e.touches[0]?.clientX;
    if (x === undefined) return;
    if (!sliderRef.current || !leftHandleRef.current || !rightHandleRef.current) return;
    const parentWidth = sliderRef.current.getBoundingClientRect().width;
    const parentLeft = sliderRef.current.getBoundingClientRect().left;

    // Calculate the percentage position relative to the slider
    const percentage = Math.max(0, Math.min(100, ((x - parentLeft) / parentWidth) * 100));

    if (draggingLeft) {
      const leftLimit =
        ((rightHandleRef.current.getBoundingClientRect().left - parentLeft) / parentWidth -
          1.0 / values.length) *
        100;
      leftHandleRef.current.style.left = `${Math.min(percentage, leftLimit)}%`;
    } else if (draggingRight) {
      const rightLimit =
        ((leftHandleRef.current.getBoundingClientRect().left - parentLeft) / parentWidth +
          1.0 / values.length) *
        100;
      rightHandleRef.current.style.left = `${Math.max(percentage, rightLimit)}%`;
    }
  };

  useEffect(() => {
    setLowLimitInput(lowLimit?.toString());
    setHighLimitInput(highLimit?.toString());
  }, [lowLimit, highLimit]);

  return (
    <div className="flex flex-col gap-4" onMouseMove={handleDrag} onTouchMove={handleDrag}>
      <ul
        // ref={ulRef}
        className={`relative mx-4 grid gap-x-1 pb-4`}
        style={{ gridTemplateColumns: `repeat(${values.length}, minmax(0, 1fr))` }}
      >
        {lowerLimitSliderPercentage !== undefined && (
          <div
            ref={leftHandleRef}
            className="bg-bd-primary absolute -bottom-1 z-10 h-4 w-4 cursor-grab rounded-full"
            style={{
              // left: lowerLimitSliderX && lowerLimitSliderX - parentX,
              left: `${lowerLimitSliderPercentage}%`,
              transform: 'translateX(-50%)',
            }}
            onMouseDown={() => setDraggingLeft(true)}
            onTouchStart={() => setDraggingLeft(true)}
          />
        )}
        {highLimitSliderPercentage !== undefined && (
          <div
            ref={rightHandleRef}
            className="bg-bd-primary absolute -bottom-1 z-10 h-4 w-4 cursor-grab rounded-full"
            style={{
              // left: highLimitSliderX && highLimitSliderX - parentX,
              left: `${highLimitSliderPercentage}%`,
              transform: 'translateX(-50%)',
            }}
            onMouseDown={() => setDraggingRight(true)}
            onTouchStart={() => setDraggingRight(true)}
          />
        )}

        <div ref={sliderRef} className="bg-bd-primary/15 absolute bottom-0 h-1.5 w-full" />
        {highLimitSliderPercentage !== undefined && lowerLimitSliderPercentage !== undefined && (
          <div
            className="bg-bd-primary/50 absolute bottom-0 h-1.5 w-full"
            style={{
              width: `${highLimitSliderPercentage - lowerLimitSliderPercentage}%`,
              left: `${lowerLimitSliderPercentage}%`,
            }}
          />
        )}

        {values.map((value, index) => (
          <li key={index} className="flex select-none flex-col items-start gap-1">
            <div className="relative flex w-full flex-row items-center justify-between gap-1">
              <p className="w-fit -translate-x-1/2 text-left text-sm">{rNumber(value)}</p>
              {index === values.length - 1 && (
                <p className="absolute right-0 w-fit translate-x-1/2 text-right text-sm">max</p>
              )}
            </div>

            {/* <div
              className={clsx('text-bd-dark w-full border-b-8 px-2 py-1 text-center text-base', {
                'border-bd-primary/50': index >= lowerLimitIdx && index <= highLimitIdx,
                'border-bd-primary/15': index < lowerLimitIdx || index > highLimitIdx
              })}
            >
              {!quantitative && (displayValues?.[index] ?? value)}
            </div> */}
          </li>
        ))}
      </ul>

      <div className="flex w-full flex-row items-center justify-between gap-1">
        <Input
          className="border-bd-primary focus:ring-bd-primary w-16 px-2 py-1 text-sm"
          value={lowLimitInput || ''}
          onChange={(e) => {
            setLowLimitInput(e.target.value);
          }}
          onBlur={(e) => {
            const value = parseFloatOrUndefined(e.target.value);
            if (
              value !== undefined &&
              value >= 0 &&
              (highLimit === undefined || value < (highLimit as number))
            ) {
              setLowLimitInput(value.toString());
              onSetLowLimitAction(value);
            } else {
              setLowLimitInput(lowLimit?.toString());
            }
          }}
        />
        <Input
          className="border-bd-primary focus:ring-bd-primary w-16 px-2 py-1 text-sm"
          value={highLimitInput || ''}
          onChange={(e) => {
            setHighLimitInput(e.target.value);
          }}
          onBlur={(e) => {
            const value = parseFloatOrUndefined(e.target.value);
            if (
              value !== undefined &&
              value >= 0 &&
              (lowLimit === undefined || value > (lowLimit as number))
            ) {
              setHighLimitInput(value.toString());
              onSetHighLimitAction(value);
            } else {
              setHighLimitInput(highLimit?.toString());
            }
          }}
        />
      </div>
    </div>
  );
}

export function DiamondQualitativeRuler({
  values,
  lowLimit,
  highLimit,
  onSetLowLimitAction,
  onSetHighLimitAction,
}: {
  values: string[];
  lowLimit: string | undefined;
  highLimit: string | undefined;
  onSetLowLimitAction: (lowLimit: string | undefined) => void;
  onSetHighLimitAction: (highLimit: string | undefined) => void;
}) {
  const [draggingLeft, setDraggingLeft] = useState(false);
  const leftHandleRef = useRef<HTMLDivElement | null>(null);
  const [draggingRight, setDraggingRight] = useState(false);
  const rightHandleRef = useRef<HTMLDivElement | null>(null);
  const ulRef = useRef<HTMLUListElement | null>(null);
  const [lowerLimitAllowedPositions, setLowerLimitAllowedPositions] = useState<
    { position: number; idx: number }[]
  >([]);
  const [highLimitAllowedPositions, setHighLimitAllowedPositions] = useState<
    { position: number; idx: number }[]
  >([]);
  const [lowerLimitSliderPercentage, setLowerLimitSliderPercentage] = useState<number | undefined>(
    undefined
  );
  const [highLimitSliderPercentage, setHighLimitSliderPercentage] = useState<number | undefined>(
    undefined
  );

  const lowLimitIdx =
    lowLimit === undefined ? 0 : values.findIndex((value) => value === lowLimit) || 0;
  const highLimitIdx =
    highLimit === undefined
      ? values.length - 1
      : values.findIndex((value) => value === highLimit) || values.length - 1;

  const calculateBoundaryPositions = useCallback(
    (lowLimitIdx: number, highLimitIdx: number) => {
      if (!ulRef.current) return;
      const liChildren = Array.from(ulRef.current.querySelectorAll('li'));
      const parentWidth = ulRef.current.getBoundingClientRect().width;
      const parentLeft = ulRef.current.getBoundingClientRect().left;
      const boundaryXs = [
        parentLeft,
        ...liChildren.map((child, idx) => {
          const nextChild = liChildren[idx + 1];
          if (!nextChild) return child.getBoundingClientRect().right;
          return (child.getBoundingClientRect().right + nextChild.getBoundingClientRect().left) / 2;
        }),
      ];
      const lowerLimitAllowedPosition: { position: number; idx: number }[] = [];
      const highLimitAllowedPosition: { position: number; idx: number }[] = [];
      boundaryXs.forEach((x, idx) => {
        if (idx <= highLimitIdx) {
          lowerLimitAllowedPosition.push({ position: x, idx });
        }
        if (idx >= lowLimitIdx + 1 && idx >= 1) {
          highLimitAllowedPosition.push({ position: x, idx: idx - 1 });
        }
      });
      setLowerLimitAllowedPositions(lowerLimitAllowedPosition);
      setHighLimitAllowedPositions(highLimitAllowedPosition);
      setLowerLimitSliderPercentage(((boundaryXs[lowLimitIdx]! - parentLeft) / parentWidth) * 100);
      setHighLimitSliderPercentage(
        ((boundaryXs[highLimitIdx + 1]! - parentLeft) / parentWidth) * 100
      );
    },
    [
      ulRef,
      setLowerLimitAllowedPositions,
      setHighLimitAllowedPositions,
      setLowerLimitSliderPercentage,
      setHighLimitSliderPercentage,
    ]
  );

  useEffect(() => {
    calculateBoundaryPositions(lowLimitIdx, highLimitIdx);
  }, [lowLimitIdx, highLimitIdx, calculateBoundaryPositions]);

  useEffect(() => {
    if (!ulRef.current) return;
    const currentUlRef = ulRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-slide-in');
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(currentUlRef);

    return () => {
      observer.unobserve(currentUlRef);
    };
  }, []);

  const findSnappedPositionAndIdx = (positions: { position: number; idx: number }[], x: number) => {
    return positions.reduce((closestPosition, position) => {
      const currentDistance = Math.abs(position.position - x);
      const closestDistance = Math.abs(closestPosition.position - x);
      return currentDistance < closestDistance ? position : closestPosition;
    }, positions[0]!);
  };

  const handleDrag = (e: React.MouseEvent | React.TouchEvent) => {
    if (!draggingLeft && !draggingRight) return;
    const x = 'clientX' in e ? e.clientX : e.touches[0]?.clientX;
    if (x === undefined) return;
    if (!ulRef.current) return;
    const parentWidth = ulRef.current.getBoundingClientRect().width;
    const parentLeft = ulRef.current.getBoundingClientRect().left;

    if (draggingLeft) {
      if (!leftHandleRef.current) return;
      const snappedPosition = findSnappedPositionAndIdx(lowerLimitAllowedPositions, x);
      leftHandleRef.current.style.left = `${((snappedPosition.position - parentLeft) / parentWidth) * 100}%`;
    } else if (draggingRight) {
      if (!rightHandleRef.current) return;
      const snappedPosition = findSnappedPositionAndIdx(highLimitAllowedPositions, x);
      rightHandleRef.current.style.left = `${((snappedPosition.position - parentLeft) / parentWidth) * 100}%`;
    }
  };

  const calculateAndSetHighLimitSliderPercentage = useCallback(() => {
    if (highLimit === undefined) {
      setHighLimitSliderPercentage(100);
    } else {
      setHighLimitSliderPercentage((highLimitIdx / values.length) * 100);
      rightHandleRef.current &&
        (rightHandleRef.current.style.left = `${(highLimitIdx / values.length) * 100}%`);
    }
  }, [highLimit, values, highLimitIdx, setHighLimitSliderPercentage, rightHandleRef]);

  const calculateAndSetLowerLimitSliderPercentage = useCallback(() => {
    if (lowLimit === undefined) {
      setLowerLimitSliderPercentage(0);
    } else {
      setLowerLimitSliderPercentage((lowLimitIdx / values.length) * 100);
      leftHandleRef.current &&
        (leftHandleRef.current.style.left = `${(lowLimitIdx / values.length) * 100}%`);
    }
  }, [lowLimit, values, lowLimitIdx, setLowerLimitSliderPercentage]);

  useEffect(() => {
    const handleDragEnd = (e: MouseEvent | TouchEvent) => {
      if (draggingLeft || draggingRight) {
        calculateAndSetHighLimitSliderPercentage();
        calculateAndSetLowerLimitSliderPercentage();
      }
    };

    document.addEventListener('mouseup', handleDragEnd);
    document.addEventListener('touchend', handleDragEnd);
    return () => {
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchend', handleDragEnd);
    };
  }, [
    draggingLeft,
    draggingRight,
    calculateAndSetHighLimitSliderPercentage,
    calculateAndSetLowerLimitSliderPercentage,
  ]);

  return (
    <div className="mx-2 flex flex-col gap-4" onMouseMove={handleDrag} onTouchMove={handleDrag}>
      <ul
        ref={ulRef}
        className={`relative grid gap-x-1`}
        style={{ gridTemplateColumns: `repeat(${values.length}, minmax(0, 1fr))` }}
      >
        {lowerLimitSliderPercentage !== undefined && (
          <div
            ref={leftHandleRef}
            className="bg-bd-primary absolute -bottom-1 z-10 h-4 w-4 cursor-grab rounded-full"
            style={{
              // left: lowerLimitSliderX && lowerLimitSliderX - parentX,
              left: `${lowerLimitSliderPercentage}%`,
              transform: 'translateX(-50%)',
            }}
            onMouseDown={() => {
              setDraggingLeft(true);
              calculateBoundaryPositions(lowLimitIdx, highLimitIdx);
            }}
            onTouchStart={() => {
              setDraggingLeft(true);
              calculateBoundaryPositions(lowLimitIdx, highLimitIdx);
            }}
          />
        )}
        {highLimitSliderPercentage !== undefined && (
          <div
            ref={rightHandleRef}
            className="bg-bd-primary absolute -bottom-1 z-10 h-4 w-4 cursor-grab rounded-full"
            style={{
              // left: highLimitSliderX && highLimitSliderX - parentX,
              left: `${highLimitSliderPercentage}%`,
              transform: 'translateX(-50%)',
            }}
            onMouseDown={() => {
              setDraggingRight(true);
              calculateBoundaryPositions(lowLimitIdx, highLimitIdx);
            }}
            onTouchStart={() => {
              setDraggingRight(true);
              calculateBoundaryPositions(lowLimitIdx, highLimitIdx);
            }}
          />
        )}
        {values.map((value, index) => (
          <li key={value} className="flex select-none flex-col items-center justify-end gap-1">
            <div
              className={clsx(
                'text-bd-dark border-b-6 flex w-full flex-row items-center justify-center px-2 py-1 text-center text-xs',
                {
                  'border-bd-primary/50': index >= lowLimitIdx && index <= highLimitIdx,
                  'border-bd-primary/15': index < lowLimitIdx || index > highLimitIdx,
                }
              )}
            >
              {value}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
